import React, {useState} from "react";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {
	Breadcrumbs,
	Button,
	Dialog, DialogContent, DialogContentText,
	DialogTitle,
	Paper,
	Stack, TextField,
	Typography
} from "@mui/material";
import {Link} from "react-router-dom";
import {GraphQlDataGrid} from "../../../components/common/grid/GraphQlDataGrid";
import {graphQLServices} from "../../../services/GraphQLServices";
import {PRODUCT_SUPPLIERS_OVERVIEW} from "../Queries";
import {formatDateTimeShort, GlobalState} from "../../../global/GlobalState";
import {productSupplierStatusColumn, textColumn, tooltipColumn} from "../../../components/common/grid/Columns";
import {productsEditorDefaultTargetStage, productsEditorTargetStage} from "../ProductModuleOverview";
import {linkProductSupplierDetailsFor, openProductSupplierDetailsForNew} from "./SuppliersDetails";
import {GridColDef, GridPagination} from "@mui/x-data-grid";
import {translateFunction} from "../../../localization/TranslationService";
import {HeaderSlot, StageSwitch} from "../../../components/onsite/Footer";

const translate = translateFunction("SureBasePortalProductsCatalog");

export const SuppliersOverview = pageAuthorizedByGroup(function () {
	const columns: GridColDef[] = [
		{
			field: "",
			headerName: translate("Link"),
			renderCell: (params) => (
				<Link to={linkProductSupplierDetailsFor(params.row.krn)} style={{
					textDecoration: "initial"
				}}>
					<Button size="small" tabIndex={-1}>{translate("Open")}</Button>
				</Link>
			),
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false,
			hideable: false,
		},
		{
			field: "krn",
			headerName: translate("Kizi Resource Name"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "name",
			headerName: translate("Name"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "legalName",
			headerName: translate("Legal Name"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "state",
			headerName: translate("State"),
			type: "singleSelect",
			minWidth: 150,
			renderCell: tooltipColumn(productSupplierStatusColumn()),
			valueOptions: ["ACTIVE", "DELETED"],
			valueFormatter: (params) => formatProductSupplierStatus(params.value),
			flex: 1,
		},
		{
			field: "creationDateTime",
			headerName: translate("Creation Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1,
		},
		{
			field: "lastModifiedBy",
			headerName: translate("Last Modified By"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "lastModifiedDateTime",
			headerName: translate("Last Modified Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1
		}
		/* todo access */
	];
	
	const stage = GlobalState.targetKiziStage(productsEditorDefaultTargetStage);
	
	return <>
		<HeaderSlot><StageSwitch defaultTargetStage={productsEditorDefaultTargetStage}/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}} to="/products">{translate("Products")}</Link>
				<Typography color="text.primary">{translate("Suppliers")}</Typography>
			</Breadcrumbs>}
			center={<Paper style={{
				height: "100%",
			}}>
				<GraphQlDataGrid
					rows={[]}
					query={PRODUCT_SUPPLIERS_OVERVIEW}
					graphFunction={"productSuppliers"}
					client={graphQLServices.productsClient}
					rowsPerPageOptions={[50, 500, 1000]}
					initialPageSize={50}
					columns={columns}
					getRowId={row => row.krn}
					defaultSortModel={[
						{
							field: "name",
							sort: "asc"
						}
					]}
					// default filter as described in SUR-136
					defaultFilterModel={{
						items: [
							{
								field: "state",
								value: "ACTIVE",
								operator: "is",
							}
						]
					}}
					queryVariables={{stage}}
					initialState={{
						columns: {
							columnVisibilityModel: {
								creationDateTime: false
							},
						},
					}}
					slots={{
						footer: CustomFooterFactory(stage)
					}}
				/>
			</Paper>}
		/>
	</>;
}, ["Admin", "Product"]);

function CustomFooterFactory(stage: string) {
	return function CustomFooter(props: any) {
		const [open, setOpen] = useState(false);
		
		const [name, setName] = useState("");
		const [legalName, setLegalName] = useState("");
		
		const handleClickOpen = () => setOpen(true);
		const handleClose = () => setOpen(false);
		
		const handleCreate = () => {
			const supplierId = generateNewSupplierId();
			const krn = `krn:kizi:product-supplier:${supplierId}`;
			setOpen(false);
			openProductSupplierDetailsForNew(krn, name, legalName);
		};
		
		return <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} style={{
			padding: "0 10px"
		}}>
			<Stack direction={"row"} spacing={2} alignItems={"center"}>
				<Button
					variant={"contained"}
					onClick={handleClickOpen}
					disabled={stage !== productsEditorTargetStage}
				>{translate("Add")}</Button>
			</Stack>
			<Dialog open={open} onClose={handleClose}>
				<DialogTitle>{translate("New Supplier")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{translate("Please provide the following information.")}
					</DialogContentText>
					<form>
						<TextField
							margin="dense"
							label={translate("Name")}
							fullWidth
							variant="standard"
							required
							value={name}
							onChange={event => {
								setName(event.target.value);
							}}
						/>
						<TextField
							margin="dense"
							label={translate("Legal Name")}
							fullWidth
							variant="standard"
							required
							value={legalName}
							onChange={event => {
								setLegalName(event.target.value);
							}}
						/>
						<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
							<Button onClick={handleClose}>{translate("Cancel")}</Button>
							<Button onClick={handleCreate} color={"success"}>{translate("Create")}</Button>
						</Stack>
					</form>
				</DialogContent>
			</Dialog>
			<GridPagination {...props} labelRowsPerPage={translate("Rows per page:")}/>
		</Stack>;
	};
}

/**
 * Generate a random uuid and take the last segment of it, assumed to be random enough.
 */
function generateNewSupplierId(): string {
	return crypto.randomUUID().split("-").last();
}

function formatProductSupplierStatus(text: string | null): string {
	return formatEnum("productSupplierStatus", text);
}

function formatEnum(prefix: string, text: string | null): string {
	if (!text)
		return "";
	return translate(text)
		?? translate("Unknown");
}
